import { createContext, useCallback, useContext, useEffect, useRef, useState, type PropsWithChildren } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useBackend } from "../backend/BackendProvider";
import type { AuthSession } from "../backend/contracts";

type AuthState =
  | { status: "loading"; session: null; error: null }
  | { status: "anonymous"; session: null; error: null }
  | { status: "authenticated"; session: AuthSession; error: null }
  | { status: "error"; session: null; error: Error };

type AuthContextValue = AuthState & {
  refresh(): Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthSessionProvider({ children }: PropsWithChildren) {
  const backend = useBackend();
  const queryClient = useQueryClient();
  const [state, setState] = useState<AuthState>({ status: "loading", session: null, error: null });
  const userId = useRef<string | null>(null);

  const applySession = useCallback((session: AuthSession | null) => {
    const nextUserId = session?.user.id ?? null;
    if (userId.current !== nextUserId) queryClient.clear();
    userId.current = nextUserId;
    setState(session
      ? { status: "authenticated", session, error: null }
      : { status: "anonymous", session: null, error: null });
  }, [queryClient]);

  const refresh = useCallback(async () => {
    setState({ status: "loading", session: null, error: null });
    try {
      applySession(await backend.auth.getSession());
    } catch (error) {
      setState({ status: "error", session: null, error: error instanceof Error ? error : new Error("Unable to check your session.") });
    }
  }, [applySession, backend]);

  useEffect(() => {
    let active = true;
    void backend.auth.getSession()
      .then((session) => { if (active) applySession(session); })
      .catch((error) => { if (active) setState({ status: "error", session: null, error: error instanceof Error ? error : new Error("Unable to check your session.") }); });
    const unsubscribe = backend.auth.subscribe((event) => {
      if (!active) return;
      applySession(event.type === "signed_out" ? null : event.session);
    });
    return () => { active = false; unsubscribe(); };
  }, [applySession, backend]);

  return <AuthContext.Provider value={{ ...state, refresh }}>{children}</AuthContext.Provider>;
}

export function useAuthSession(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("AuthSessionProvider is missing.");
  return value;
}
